import { useEffect, useRef, ReactNode } from 'react';
import {
  widget,
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  ResolutionString,
  Timezone,
} from 'charting_library';
import {
  getCurrentTimezone,
  getLanguageFromURL,
  getSymbolFromAthleteContract,
  getTimeFrameByResolution,
  getVisibleRange,
} from './helpers';
import { WidgetRefT } from './hooks';
import { timeFrames } from './constants';
import useDatafeed from './datafeed';

export interface ChartContainerPropsI {
  symbol: string;
  interval: string;

  // BEWARE: no trailing slash is expected in feed URL
  datafeedUrl: string;
  chartsStorageUrl: ChartingLibraryWidgetOptions['charts_storage_url'];
  chartsStorageApiVersion: ChartingLibraryWidgetOptions['charts_storage_api_version'];
  clientId: ChartingLibraryWidgetOptions['client_id'];
  userId: ChartingLibraryWidgetOptions['user_id'];
  theme: ChartingLibraryWidgetOptions['theme'];
  disableTopToolbar: boolean;
  disableLeftToolbar: boolean;
  className: string;
  tvWidgetRef: WidgetRefT;
  height: ChartingLibraryWidgetOptions['height'];
  customHeader: (tvWidget: WidgetRefT) => ReactNode;
}

export const useTvWidget = (
  props: Partial<ChartContainerPropsI> & { symbol: string; tradingPairId: string }
) => {
  const initialPropsRef = useRef<Partial<ChartContainerPropsI & { timeFrame: string }>>({
    ...props,
    symbol: getSymbolFromAthleteContract(props.symbol, props.tradingPairId),
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const tvWidgetRef = useRef<IChartingLibraryWidget | null>(null);
  const Datafeed = useDatafeed();

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const {
      interval = '1D' as ResolutionString,
      datafeedUrl = '',
      chartsStorageUrl = 'https://saveload.tradingview.com',
      chartsStorageApiVersion = '1.1',
      clientId = 'tradingview.com',
      userId = 'public_user_id',
      theme = 'Dark',
      disableTopToolbar,
      disableLeftToolbar,
      height,
    } = initialPropsRef.current;

    const timeFrame = getTimeFrameByResolution(interval);

    initialPropsRef.current = {
      symbol: getSymbolFromAthleteContract(props.symbol, props.tradingPairId), // Override default symbol with symbol from props
      interval: timeFrame.resolution,
      datafeedUrl,
      chartsStorageUrl,
      chartsStorageApiVersion,
      clientId,
      userId,
      theme,
      disableTopToolbar,
      disableLeftToolbar,
      height,
      timeFrame: timeFrame.text,
    };

    const widgetOptions: ChartingLibraryWidgetOptions = {
      container: containerRef.current,
      // BEWARE: no trailing slash is expected in feed URL
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // datafeed: new (window as any).Datafeeds.UDFCompatibleDatafeed(datafeedUrl),
      datafeed: Datafeed,
      interval: timeFrame.resolution,
      symbol: getSymbolFromAthleteContract(props.symbol, props.tradingPairId),
      // auto_save_delay: 10000,
      autosize: true,
      debug: false,
      disabled_features: [
        ...(!disableTopToolbar ? [] : ['header_widget']),
        ...(!disableLeftToolbar ? [] : ['left_toolbar']),
        'use_localstorage_for_settings',
        'header_chart_type',
        'symbol_info',
        'source_selection_markers',
        'header_symbol_search',
        'symbol_search_hot_key',
        'header_resolutions',
        'header_indicators',
        'header_compare',
        'header_undo_redo',
        'header_interval_dialog_button',
        'header_settings',
        'header_screenshot',
        'header_fullscreen_button',
        'header_saveload',
        'popup_hints',
        'show_interval_dialog_on_key_press',
      ],
      enabled_features: [
        'end_of_period_timescale_marks',
        'hide_left_toolbar_by_default',
        'hide_resolution_in_legend',
      ],
      fullscreen: false,
      height,
      library_path: '/charting_library/',
      locale: getLanguageFromURL() || 'en',
      study_count_limit: 2,
      symbol_search_request_delay: 500,
      timeframe: timeFrame.text,
      timezone: getCurrentTimezone() as Timezone,
      time_frames: timeFrames.map(({ realResolution, ...rest }) => rest),
      toolbar_bg: 'var(--hcx-panel)',
      charts_storage_url: chartsStorageUrl,
      charts_storage_api_version: chartsStorageApiVersion,
      client_id: clientId,
      user_id: userId,
      // load_last_chart: true,
      theme,
      header_widget_buttons_mode: 'fullsize',
    };

    const tvWidget = new widget(widgetOptions);

    let removeEventListeners: (() => void)[];
    tvWidget.onChartReady(() => {
      /* tvWidget.headerReady().then(function () {
        createButton(tvWidget, {
          description: 'Save chart image',
          content: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.5 9H3V12.75H15V9H16.5V12.75C16.5 13.5825 15.8325 14.25 15 14.25H3C2.175 14.25 1.5 13.5825 1.5 12.75V9ZM9 11.25L13.1625 7.155L12.0975 6.0975L9.75 8.4375V1.5H8.25V8.4375L5.91 6.0975L4.845 7.1625L9 11.25Z" fill="#9E9EA3"/>
          </svg>`,
          onClick: () => {
            saveChartAsImg(tvWidget);
          },
        });
        createButton(tvWidget, {
          description: 'Chart properties',
          content: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.00012 5C8.79577 5 9.55883 5.31607 10.1214 5.87868C10.6841 6.44129 11.0001 7.20435 11.0001 8C11.0001 8.79565 10.6841 9.55871 10.1214 10.1213C9.55883 10.6839 8.79577 11 8.00012 11C7.20447 11 6.44141 10.6839 5.8788 10.1213C5.31619 9.55871 5.00012 8.79565 5.00012 8C5.00012 7.20435 5.31619 6.44129 5.8788 5.87868C6.44141 5.31607 7.20447 5 8.00012 5ZM8.00012 6.5C7.6023 6.5 7.22077 6.65804 6.93946 6.93934C6.65816 7.22064 6.50012 7.60218 6.50012 8C6.50012 8.39782 6.65816 8.77936 6.93946 9.06066C7.22077 9.34196 7.6023 9.5 8.00012 9.5C8.39795 9.5 8.77948 9.34196 9.06078 9.06066C9.34209 8.77936 9.50012 8.39782 9.50012 8C9.50012 7.60218 9.34209 7.22064 9.06078 6.93934C8.77948 6.65804 8.39795 6.5 8.00012 6.5ZM6.50012 15.5C6.31262 15.5 6.15512 15.365 6.12512 15.185L5.84762 13.1975C5.37512 13.01 4.97012 12.755 4.58012 12.455L2.71262 13.2125C2.54762 13.2725 2.34512 13.2125 2.25512 13.0475L0.755124 10.4525C0.657624 10.2875 0.702624 10.085 0.845124 9.9725L2.42762 8.7275L2.37512 8L2.42762 7.25L0.845124 6.0275C0.702624 5.915 0.657624 5.7125 0.755124 5.5475L2.25512 2.9525C2.34512 2.7875 2.54762 2.72 2.71262 2.7875L4.58012 3.5375C4.97012 3.245 5.37512 2.99 5.84762 2.8025L6.12512 0.815C6.15512 0.635 6.31262 0.5 6.50012 0.5H9.50012C9.68762 0.5 9.84512 0.635 9.87512 0.815L10.1526 2.8025C10.6251 2.99 11.0301 3.245 11.4201 3.5375L13.2876 2.7875C13.4526 2.72 13.6551 2.7875 13.7451 2.9525L15.2451 5.5475C15.3426 5.7125 15.2976 5.915 15.1551 6.0275L13.5726 7.25L13.6251 8L13.5726 8.75L15.1551 9.9725C15.2976 10.085 15.3426 10.2875 15.2451 10.4525L13.7451 13.0475C13.6551 13.2125 13.4526 13.28 13.2876 13.2125L11.4201 12.4625C11.0301 12.755 10.6251 13.01 10.1526 13.1975L9.87512 15.185C9.84512 15.365 9.68762 15.5 9.50012 15.5H6.50012ZM7.43762 2L7.16012 3.9575C6.26012 4.145 5.46512 4.625 4.88762 5.2925L3.08012 4.5125L2.51762 5.4875L4.10012 6.65C3.80012 7.5275 3.80012 8.48 4.10012 9.35L2.51012 10.52L3.07262 11.495L4.89512 10.715C5.47262 11.375 6.26012 11.855 7.15262 12.035L7.43012 14H8.57012L8.84762 12.0425C9.74012 11.855 10.5276 11.375 11.1051 10.715L12.9276 11.495L13.4901 10.52L11.9001 9.3575C12.2001 8.48 12.2001 7.5275 11.9001 6.65L13.4826 5.4875L12.9201 4.5125L11.1126 5.2925C10.5351 4.625 9.74012 4.145 8.84012 3.965L8.56262 2H7.43762Z" fill="#9E9EA3"/>
          </svg>`,
          onClick: () => {
            openChartSettings(tvWidget);
          },
        });
        createButton(tvWidget, {
          description: 'Copy chart image',
          content: `<svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H11.6225L10.25 0.5H5.75L4.3775 2H2C1.60218 2 1.22064 2.15803 0.93934 2.43934C0.658035 2.72064 0.5 3.10217 0.5 3.5V12.5C0.5 12.8978 0.658035 13.2794 0.93934 13.5607C1.22064 13.842 1.60218 14 2 14H14C14.3978 14 14.7794 13.842 15.0607 13.5607C15.342 13.2794 15.5 12.8978 15.5 12.5V3.5C15.5 3.10217 15.342 2.72064 15.0607 2.43934C14.7794 2.15803 14.3978 2 14 2ZM14 12.5H2V3.5H5.0375L6.41 2H9.59L10.9625 3.5H14V12.5ZM8 4.25C7.00544 4.25 6.05161 4.64509 5.34835 5.34835C4.64509 6.05161 4.25 7.00544 4.25 8C4.25 8.99456 4.64509 9.94839 5.34835 10.6516C6.05161 11.3549 7.00544 11.75 8 11.75C8.99456 11.75 9.94839 11.3549 10.6517 10.6516C11.3549 9.94839 11.75 8.99456 11.75 8C11.75 7.00544 11.3549 6.05161 10.6517 5.34835C9.94839 4.64509 8.99456 4.25 8 4.25ZM8 10.25C7.40326 10.25 6.83097 10.0129 6.40901 9.59099C5.98705 9.16903 5.75 8.59674 5.75 8C5.75 7.40326 5.98705 6.83096 6.40901 6.40901C6.83097 5.98705 7.40326 5.75 8 5.75C8.59674 5.75 9.16903 5.98705 9.59099 6.40901C10.0129 6.83096 10.25 7.40326 10.25 8C10.25 8.59674 10.0129 9.16903 9.59099 9.59099C9.16903 10.0129 8.59674 10.25 8 10.25Z" fill="#9E9EA3"/>
          </svg>`,
          onClick: () => {
            takeChartScreenshot(tvWidget);
          },
        });
        createButton(tvWidget, {
          description: 'Fullscreen mode',
          content: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.125 7.8175L6.1825 8.875L2.8075 12.25H5.5V13.75H0.25V8.5H1.75V11.1925L5.125 7.8175ZM6.1825 5.125L5.125 6.1825L1.75 2.8075V5.5H0.25V0.25H5.5V1.75H2.8075L6.1825 5.125ZM8.875 7.8175L12.25 11.1925V8.5H13.75V13.75H8.5V12.25H11.1925L7.8175 8.875L8.875 7.8175ZM7.8175 5.125L11.1925 1.75H8.5V0.25H13.75V5.5H12.25V2.8075L8.875 6.1825L7.8175 5.125Z" fill="#9E9EA3"/>
          </svg>`,
          onClick: () => {
            makeChartFullScreen(tvWidget);
          },
        });
        createDropdown(tvWidget, {
          title: 'Fullscreen mode',
          description: 'Fullscreen mode',
          options: [
            {
              title: 'Select me',
              onSelect: () => {
                setNewSymbolToChart(symbols[randomIntFromInterval(0, symbols.length - 1)], tvWidget);
              },
            },
          ],
          icon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 5px;">
          <path d="M5.125 7.8175L6.1825 8.875L2.8075 12.25H5.5V13.75H0.25V8.5H1.75V11.1925L5.125 7.8175ZM6.1825 5.125L5.125 6.1825L1.75 2.8075V5.5H0.25V0.25H5.5V1.75H2.8075L6.1825 5.125ZM8.875 7.8175L12.25 11.1925V8.5H13.75V13.75H8.5V12.25H11.1925L7.8175 8.875L8.875 7.8175ZM7.8175 5.125L11.1925 1.75H8.5V0.25H13.75V5.5H12.25V2.8075L8.875 6.1825L7.8175 5.125Z" fill="#9E9EA3"/>
          </svg>`,
        });
      }); */
    });

    tvWidgetRef.current = tvWidget;
    if (props.tvWidgetRef) {
      props.tvWidgetRef.current = tvWidget;
    }

    return () => {
      if (tvWidgetRef.current) {
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
        if (props.tvWidgetRef?.current) {
          props.tvWidgetRef.current = null;
        }
      }
      if (removeEventListeners?.length) {
        removeEventListeners.forEach((item) => item());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tvWidgetRef.current && props.theme) {
      initialPropsRef.current.theme = props.theme;
      const currentTheme = tvWidgetRef.current.getTheme();
      if (currentTheme !== props.theme) {
        tvWidgetRef.current.changeTheme(props.theme, { disableUndo: true });
      }
    }
  }, [props.theme]);

  useEffect(() => {
    if (tvWidgetRef.current && props.interval) {
      const timeFrame = getTimeFrameByResolution(props.interval);

      if (initialPropsRef.current.interval !== timeFrame.resolution) {
        initialPropsRef.current.interval = timeFrame.resolution;
        tvWidgetRef.current.activeChart().setResolution(timeFrame.resolution);
      }

      if (initialPropsRef.current.timeFrame !== timeFrame.text) {
        initialPropsRef.current.timeFrame = timeFrame.text;
        const { to } = tvWidgetRef.current.activeChart().getVisibleRange();
        tvWidgetRef.current
          .activeChart()
          .setVisibleRange(getVisibleRange(timeFrame.realResolution, to));
      }
    }
  }, [props.interval]);

  useEffect(() => {
    if (tvWidgetRef.current && props.symbol) {
      const symbol = getSymbolFromAthleteContract(props.symbol, props.tradingPairId);
      if (initialPropsRef.current.symbol !== symbol) {
        initialPropsRef.current.symbol = symbol;
        tvWidgetRef.current.activeChart().setSymbol(symbol);
      }
    }
  }, [props.theme, props.interval, props.symbol, props.tradingPairId]);

  return { containerRef, tvWidgetRef };
};
